import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay/hooks";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";

import type { Login_login_Mutation } from "api/__generated__/Login_login_Mutation.graphql";
import { useAuth } from "contexts/Auth";
import AuthPage from "components/AuthPage";
import InputPassword from "components/InputPassword";
import Spinner from "components/Spinner";
import { Link, Route } from "Navigation";

const LOGIN_MUTATION = graphql`
  mutation Login_login_Mutation($input: LoginInput!) {
    login(input: $input) {
      token
    }
  }
`;

interface FormData {
  email: string;
  password: string;
}

const initialFormData: FormData = {
  email: "",
  password: "",
};

const Login = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validated, setValidated] = useState(false);
  const [errorFeedback, setErrorFeedback] = useState<React.ReactNode>(null);
  const intl = useIntl();
  const user = useAuth();
  const [login, isLoggingIn] =
    useMutation<Login_login_Mutation>(LOGIN_MUTATION);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const form = event.currentTarget;
      if (form.checkValidity() === false) {
        return setValidated(true);
      }
      login({
        variables: {
          input: { email: formData.email, password: formData.password },
        },
        onCompleted(data, errors) {
          if (errors) {
            const errorFeedback = errors
              .map((error) => error.message)
              .join(". \n");
            setErrorFeedback(errorFeedback);
          }
          user.setAuthToken(data.login?.token || null);
        },
        onError(error) {
          setErrorFeedback(
            <FormattedMessage
              id="pages.Login.form.loadingErrorFeedback"
              defaultMessage="Could not login, please try again."
              description="Feedback for unknown loading error in the Login page"
            />
          );
        },
      });
    },
    [formData, login, user]
  );

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback((event) => {
      const target = event.target;
      const value = target.type === "checkbox" ? target.checked : target.value;
      const field = target.id;
      setFormData((data) => ({ ...data, [field]: value }));
    }, []);

  return (
    <AuthPage>
      <Alert
        show={!!errorFeedback}
        variant="danger"
        onClose={() => setErrorFeedback(null)}
        dismissible
      >
        {errorFeedback}
      </Alert>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Stack gap={3}>
          <Form.Group controlId="email">
            <Form.Control
              value={formData.email}
              onChange={handleInputChange}
              required
              type="email"
              placeholder={intl.formatMessage({
                id: "pages.Login.form.emailPlaceholder",
                defaultMessage: "Email",
                description:
                  "Placeholder for the email field in the Login page",
              })}
            />
            <Form.Control.Feedback type="invalid">
              <FormattedMessage
                id="pages.Login.form.missingEmailFeedback"
                defaultMessage="Please provide a valid email."
                description="Feedback for empty email field in the Login page"
              />
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="password">
            <InputPassword
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder={intl.formatMessage({
                id: "pages.Login.form.passwordPlaceholder",
                defaultMessage: "Password",
                description:
                  "Placeholder for the password field in the Login page",
              })}
            >
              <Form.Control.Feedback type="invalid">
                <FormattedMessage
                  id="pages.Login.form.missingPasswordFeedback"
                  defaultMessage="Please provide a password."
                  description="Feedback for empty password field in the Login page"
                />
              </Form.Control.Feedback>
            </InputPassword>
          </Form.Group>
          <Button
            variant="primary"
            type="submit"
            className="w-100 mt-3"
            disabled={isLoggingIn}
          >
            {isLoggingIn && <Spinner size="sm" className="me-2" />}
            <FormattedMessage
              id="pages.Login.form.passwordLoginButton"
              defaultMessage="Login"
              description="Title for the button to login with password in the Login Page"
            />
          </Button>
        </Stack>
        <div className="mt-5 text-center">
          <p>
            <FormattedMessage
              id="pages.Login.form.registerLink"
              defaultMessage="No account yet? <registerLink>Register</registerLink>"
              description="Title for the link to Register in the Login Page"
              values={{
                registerLink: (chunks: React.ReactNode) => (
                  <Link route={Route.register}>{chunks}</Link>
                ),
              }}
            />
          </p>
        </div>
      </Form>
    </AuthPage>
  );
};

export default Login;
